import React, { PropsWithChildren, ReactNode } from 'react';
import type { AnyAction, Store } from 'redux';
import { render, RenderResult } from '@testing-library/react';
import { Provider } from 'react-redux';
import {
  BimTrackThemeProvider,
  FontSet,
  OverlayBanner,
  OverlayBannerProvider,
  ResetCSS,
} from '@bimone/bimtrackreactcomponents';
import { I18n as I18nPage, I18nProvider, I18n, Language } from '@bimone/bimtrack-core';
import { applicationStore } from '../../app/redux/store';
import LocaleSettings from '../culture/LocaleSettings';
import { Resource } from 'i18next';

export type rerender = (ui: React.ReactElement) => void;

export type WrapperProps = PropsWithChildren<{
  store?: Store;
  i18n: I18n;
}>;

const configureStore = (): Store => applicationStore;

export const Wrapper = ({ children, store = configureStore(), i18n }: WrapperProps): JSX.Element => {
  return (
    <BimTrackThemeProvider>
      <ResetCSS singlePageApp />
      <FontSet />
      <Provider store={store}>
        <OverlayBannerProvider>
          <OverlayBanner />
          <I18nProvider i18n={i18n}>{children}</I18nProvider>
        </OverlayBannerProvider>
      </Provider>
    </BimTrackThemeProvider>
  );
};

export class PageRenderer {
  private static readonly COMMON_NAMESPACE = 'common';

  private constructor(
    private readonly wrapperComponent: React.ComponentType<WrapperProps>,
    public readonly store: Store,
    public readonly i18n: I18n
  ) {}

  public static async create(
    pageTranslations: Resource,
    wrapperComponent: React.ComponentType<WrapperProps> = Wrapper,
    language: Language = Language.EN,
    store: Store = configureStore()
  ): Promise<PageRenderer> {
    const i18n = await I18nPage.createOther(
      pageTranslations,
      PageRenderer.COMMON_NAMESPACE,
      PageRenderer.COMMON_NAMESPACE
    );

    const localSettings = new LocaleSettings(i18n);

    localSettings.setupLocaleSpecifications();
    localSettings.setCurrentLocale(language);
    return new PageRenderer(wrapperComponent, store, i18n);
  }

  public dispatchAction(action: AnyAction): void {
    this.store.dispatch(action);
  }

  public mount(componentNode: ReactNode): RenderResult {
    const WrapperComponent = this.wrapperComponent;

    return render(
      <WrapperComponent
        i18n={this.i18n}
        store={this.store}
      >
        {componentNode}
      </WrapperComponent>
    );
  }

  public update(componentNode: ReactNode, rerender: rerender): void {
    const WrapperComponent = this.wrapperComponent;

    rerender(
      <WrapperComponent
        store={this.store}
        i18n={this.i18n}
      >
        {componentNode}
      </WrapperComponent>
    );
  }
}
